import React, { useEffect, useRef, useState } from "react";
import Resumable from "resumablejs";
import { postData } from "../axios/apiHelper";
import { Modal, Button } from "react-bootstrap";
import { CircularProgressbar } from "react-circular-progressbar";
import { toast } from "react-toastify";

const MAX_SIZE_MB = 1024;
const CHUNK_SIZE = 5 * 1024 * 1024;
const RETRY_LIMIT = 3;
const ALLOWED_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "video/mp4",
];

const ResumableUploader = ({
  label,
  handleFileSuccessCallback,
  payload = {},
  accept,
}) => {
  const browseRef = useRef(null);
  const resumableRef = useRef(null);
  const uploadId = useRef(null);
  const s3Key = useRef(null);
  const retryCount = useRef(0);
  const activeUpload = useRef(null);
  const fileType = useRef(null);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [uploadSpeed, setUploadSpeed] = useState(null);

  const lastTimeRef = useRef(null);
  const lastLoadedRef = useRef(0);

  const initUploadSession = async (file) => {
    try {
      setStatus("Initializing upload...");
      let type = "document";
      if (file.type.startsWith("image/")) type = "image";
      if (file.type.startsWith("video/")) type = "video";
      if (file.type.startsWith("audio/")) type = "audio";
      fileType.current = type;

      const response = await postData("/onesource/init-upload", {
        filename: file.name,
        type,
        ...payload,
      });

      if (!response.data?.uploadId) throw new Error("Invalid upload ID");
      uploadId.current = response.data.uploadId;
      s3Key.current = response.data.s3Key;
      return true;
    } catch (error) {
      console.error("Initialization error:", error);
      setStatus("Initialization failed");
      return false;
    }
  };

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      setStatus("Invalid file type");
      return false;
    }

    if (file.size > MAX_SIZE_MB * 1024 * 1024 * 3) {
      setStatus(`File too large (max ${MAX_SIZE_MB}MB)`);
      return false;
    }

    return true;
  };

  const handleFileAdded = async (file) => {
    if (activeUpload.current) {
      resumableRef.current.removeFile(file);
      return;
    }

    if (!validateFile(file.file)) {
      resumableRef.current.removeFile(file);
      return;
    }

    setIsUploading(true);
    const initialized = await initUploadSession(file.file);
    if (!initialized) return;

    activeUpload.current = file;

    retryCount.current = 0;
    resumableRef.current.opts.query = { uploadId: uploadId.current };
    resumableRef.current.opts.testChunks = false;

    setStatus("Uploading...");
    setUploadProgress(0);
    lastTimeRef.current = Date.now();
    lastLoadedRef.current = 0;
    resumableRef.current.upload();
  };

  const calculateSpeed = (file) => {
    const now = Date.now();
    const timeElapsed = (now - lastTimeRef.current) / 1000;
    const bytesUploaded = file.size * file.progress();
    const delta = bytesUploaded - lastLoadedRef.current;

    if (timeElapsed > 0) {
      const speedBps = delta / timeElapsed;
      const speedMbps = (speedBps * 8) / (1024 * 1024);
      if (Math.floor(speedMbps) !== 0) {
        setUploadSpeed(speedMbps.toFixed(2));
      }
    }

    lastTimeRef.current = now;
    lastLoadedRef.current = bytesUploaded;
    setUploadProgress(Math.floor(file.progress() * 100));
  };

  const handleFileProgress = (file) => {
    if (file !== activeUpload.current) return;
    calculateSpeed(file);
  };

  const completeUpload = async (file) => {
    try {
      setStatus("Finalizing...");
      const response = await postData("/onesource/complete-upload", {
        uploadId: uploadId.current,
        filename: file.fileName,
        type: fileType.current,
      });

      if (!response.data?.url) throw new Error("Invalid response");
      handleFileSuccessCallback(response.data.url);
      setStatus("Upload complete");
      toast.success("File uploaded successfully!");
    } catch (err) {
      console.error("Completion error:", err);
      if (retryCount.current < RETRY_LIMIT) {
        retryCount.current += 1;
        setStatus(`Retrying (${retryCount.current}/${RETRY_LIMIT})...`);
        setTimeout(
          () => resumableRef.current.upload(),
          1000 * retryCount.current
        );
        return;
      }
      setStatus("Finalization failed");
      toast.error("File upload failed. Please try again.");
    } finally {
      resetUploadState();
    }
  };

  const handleFileSuccess = (file) => {
    if (file !== activeUpload.current) return;
    completeUpload(file);
  };

  const handleFileError = (file, message) => {
    if (file !== activeUpload.current) return;
    console.error("Upload error:", message);
    if (retryCount.current < RETRY_LIMIT) {
      retryCount.current += 1;
      setStatus(`Retrying (${retryCount.current}/${RETRY_LIMIT})...`);
      setTimeout(
        () => resumableRef.current.upload(),
        1000 * retryCount.current
      );
      return;
    }
    setStatus("Upload failed");
    resetUploadState();
  };

  const resetUploadState = () => {
    setIsUploading(false);
    setUploadProgress(0);
    setIsPaused(false);
    setUploadSpeed(null);
    uploadId.current = null;
    s3Key.current = null;
    activeUpload.current = null;
    retryCount.current = 0;
  };

  const handlePauseResume = () => {
    if (!resumableRef.current || !activeUpload.current) return;
    if (isPaused) {
      resumableRef.current.resume();
      setStatus("Resumed...");
    } else {
      resumableRef.current.pause();
      setStatus("Paused");
    }
    setIsPaused(!isPaused);
  };

  const handleCancel = () => {
    if (!resumableRef.current || !activeUpload.current) return;
    resumableRef.current.cancel();
    setStatus("Cancelled");
    resetUploadState();
  };

  useEffect(() => {
    const r = new Resumable({
      target: `${process.env.REACT_APP_API_KEY_NEW_DESIGN}/onesource/upload`,
      query: {},
      fileParameterName: "file",
      chunkSize: CHUNK_SIZE,
      simultaneousUploads: 5,
      testChunks: false,
      throttleProgressCallbacks: 1,
      maxFiles: 1,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        auth: localStorage.getItem("decrypted_token"),
      },
      fileType: accept ? [accept] : ALLOWED_TYPES,
    });

    r.assignBrowse(browseRef.current);
    r.on("fileAdded", handleFileAdded);
    r.on("fileProgress", handleFileProgress);
    r.on("fileSuccess", handleFileSuccess);
    r.on("fileError", handleFileError);
    resumableRef.current = r;

    return () => {
      if (activeUpload.current) {
        r.cancel();
      }
    };
  }, []);

  return (
    <>
      <Button ref={browseRef} variant="primary" disabled={isUploading}>
        {isUploading ? "Uploading..." : label}
      </Button>

      <Modal
        show={isUploading}
        backdrop="static"
        keyboard={false}
        centered
        id="progress_modal"
        children={false}
        className="send-confirm"
      >
        <Modal.Header>
          <Modal.Title>Uploading File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="circular-progressbar">
            <CircularProgressbar
              value={uploadProgress}
              text={`${uploadProgress}%`}
              strokeWidth={5}
            />
          </div>
          {/* {uploadSpeed && <div>Speed: {uploadSpeed} Mbps</div>} */}
          {/* <div>{status}</div> */}
          <div className="modal-buttons">
            {/* <Button variant="secondary" onClick={handlePauseResume}>
              {isPaused ? "Resume" : "Pause"}
            </Button> */}
            <Button onClick={handleCancel} className="btn-bordered light">
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ResumableUploader;
